import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Backdrop,
  CircularProgress,
  TablePagination,
  Grid,
  DialogActions,
  DialogContent,
  Button,
  Box,
  ButtonGroup,
  Typography,
  Select,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { StyledDialog } from "../StyledMui";
import useStore from "../Store";
import NewTemplateDialog from "./NewTemplateDialog";
import ApiRequest from "../ApiRequest";
import AlertDialog from "../ReusableComponents/AlertDialog";
import { useAlert } from "../AlertProvider";

function ManageTemplateModal() {
  // Store 및 Provider에서 필요한 값 가져오기
  const {
    BASIC_ALERT_DURATION,
    setTemplateEditMode,
    closeModal,
    templateModal,
    openNewTemplateDialog,
    getTemplates,
    templates,
    selectedChannel,
    selectedChannelName,
  } = useStore((state) => state.header);

  const { actId, queryMappingTable } = useStore((state) => state.mapper);
  const { showAlert } = useAlert();

  // Local state
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [alertDialog, setAlertDialog] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loadingModal, setLoadingModal] = useState(false);

  // 모달이 열릴 때 템플릿 목록 새로고침
  useEffect(() => {
    if (templateModal) {
      getTemplates();
      setCurrentPage(0);
    }
  }, [templateModal, selectedChannelName, actId, queryMappingTable, getTemplates]);

  // 템플릿 필터링 (채널, 유형, 검색어)
  const filteredTemplates = useMemo(() => {

    const filterByType = (template) =>      
      selectedType === "all" || template.templateType === selectedType;

    let list = templates.filter(filterByType);

    if (["DBU", "RBU", "OBU"].includes(selectedChannelName)) {
      return templates.filter(filterByType);
    }

    const kw = searchKeyword.toLowerCase();
    return list.filter((template) => {
      const code = (template.templateCode ?? "").toString().toLowerCase();
      const name = (template.templateName ?? "").toString().toLowerCase();
      return code.includes(kw) || name.includes(kw);
    });
  }, [templates, selectedType, searchKeyword, selectedChannelName]);  

  const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);

  const currentTemplates = useMemo(
    () =>
      filteredTemplates.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
      ),
    [filteredTemplates, currentPage, itemsPerPage]
  );

  // 핸들러들
  const handleSearchKeywordChange = useCallback((event) => {
    setSearchKeyword(event.target.value);
    setCurrentPage(0);
  }, []);

  const handleTypeChange = useCallback((event) => {
    setSelectedType(event.target.value);
    setCurrentPage(0);
  }, []);

  const handleInspectionRequest = useCallback(
    async (index) => {
      setLoadingModal(true);
      const template = currentTemplates[index];
      const requestData = {
        senderKey: template.senderKey,
        templateCode: template.templateCode,
      };
      try {
        const requestEndpoint =
          "/biztalkCenterApi/alimtalk/template/request";
        const apiResponse = await ApiRequest.post(requestEndpoint, requestData);
        if (apiResponse.code === "200") {
          const updateData = {
            tableName: "template",
            data: { inspectionStatus: "REQ", status: "R" },
            filter: { id: template.Id },
          };
          await ApiRequest.post("/db/updateData", updateData);
          getTemplates();
          showAlert(
            "검수요청이 정상적으로 접수되었습니다.",
            "success",
            BASIC_ALERT_DURATION
          );
        } else {
          showAlert(apiResponse.message, "error", BASIC_ALERT_DURATION);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingModal(false);
      }
    },
    [currentTemplates, getTemplates, showAlert, BASIC_ALERT_DURATION]
  );

  const handleInspectionRequestCancel = useCallback(
    async (index) => {
      setLoadingModal(true);
      const template = currentTemplates[index];
      const requestData = {
        senderKey: template.senderKey,
        templateCode: template.templateCode,
      };
      try {
        const cancelEndpoint =
          "/biztalkCenterApi/alimtalk/template/cancel_request";
        const apiResponse = await ApiRequest.post(cancelEndpoint, requestData);
        if (
          apiResponse.code === "200" ||
          (apiResponse.code === "509" &&
            apiResponse.message === "이미 검수가 취소된 템플릿입니다.")
        ) {
          const updateData = {
            tableName: "template",
            data: { inspectionStatus: "REG", status: "R" },
            filter: { id: template.Id },
          };
          await ApiRequest.post("/db/updateData", updateData);
          getTemplates();
          showAlert(
            "검수요청이 정상적으로 취소되었습니다.",
            "success",
            BASIC_ALERT_DURATION
          );
        } else {
          showAlert(apiResponse.message, "error", BASIC_ALERT_DURATION);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingModal(false);
      }
    },
    [currentTemplates, getTemplates, showAlert, BASIC_ALERT_DURATION]
  );

  const handleTemplateDelete = useCallback(
    (index) => {
      setTemplateToDelete(currentTemplates[index]);
      setAlertDialog(true);
    },
    [currentTemplates]
  );

  const agreeAlertDialog = useCallback(async () => {
    setLoadingModal(true);
    try {
      let apiResponse;
      if (templateToDelete.templateType === "alimtalk") {
        const data = {
          senderKey: templateToDelete.senderKey,
          templateCode: templateToDelete.templateCode,
        };
        apiResponse = await ApiRequest.post(
          "/biztalkCenterApi/alimtalk/template/delete",
          data
        );
      }
      if (
        templateToDelete.templateType === "friendtalk" ||
        apiResponse?.code === "200" ||
        (apiResponse?.code === "508" &&
          apiResponse.message === "요청한 데이터가 존재하지 않습니다.")
      ) {
        const deleteData = {
          tableName: "template",
          filter: { id: templateToDelete.Id },
        };
        await ApiRequest.post("/db/deleteData", deleteData);
        setAlertDialog(false);
        getTemplates();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
    }
  }, [templateToDelete, getTemplates]);

  return (
    <>
      <StyledDialog
        open={templateModal}
        onClose={() => closeModal("templateModal")}
      >
        <DialogContent>
          {loadingModal && (
            <Backdrop open>
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
          <Box p={2} display="flex" justifyContent="space-between">
            <Grid container spacing={2}>
              {/* 상단 타이틀 및 버튼 */}
              <Grid item xs={6}>
                <Typography variant="h6">템플릿 관리</Typography>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" justifyContent="flex-end">
                  <ButtonGroup variant="contained">
                    <Button onClick={openNewTemplateDialog}>생성</Button>
                  </ButtonGroup>
                </Box>
              </Grid>
              {/* 유형 선택 및 검색 */}
              <Grid item xs={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="type-label">유형 선택</InputLabel>
                  <Select
                    labelId="type-label"
                    label="유형 선택"
                    value={selectedType}
                    onChange={handleTypeChange}
                  >
                    <MenuItem value="all">전체</MenuItem>
                    <MenuItem value="alimtalk">알림톡</MenuItem>
                    <MenuItem value="friendtalk">친구톡</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={10}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="search">검색</InputLabel>
                  <OutlinedInput
                    id="search"
                    value={searchKeyword}
                    onChange={handleSearchKeywordChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton type="submit" edge="end" aria-label="search">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    }
                    label="검색"
                  />
                </FormControl>
              </Grid>
              {/* 템플릿 테이블 */}
              <Grid item xs={12}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>채널</TableCell>
                      <TableCell>템플릿유형</TableCell>
                      <TableCell>템플릿코드</TableCell>
                      <TableCell>템플릿명</TableCell>
                      <TableCell>메세지유형</TableCell>
                      <TableCell>강조유형</TableCell>
                      <TableCell>검수상태</TableCell>
                      <TableCell>액션</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentTemplates.length > 0 ? (
                      currentTemplates.map((template, index) => (
                        <TableRow key={template.Id || index}>
                          <TableCell>{template.channelName}</TableCell>
                          <TableCell>{template.templateType}</TableCell>
                          <TableCell>{template.templateCode}</TableCell>
                          <TableCell>{template.templateName}</TableCell>
                          <TableCell>{template.templateMessageType}</TableCell>
                          <TableCell>{template.templateEmphasizeType}</TableCell>
                          <TableCell>{template.inspectionStatus}</TableCell>
                          <TableCell>
                            <ButtonGroup>
                              {template.inspectionStatus === "REG" &&
                                template.status === "R" && (
                                  <Button
                                    variant="contained"
                                    onClick={() =>
                                      handleInspectionRequest(index)
                                    }
                                  >
                                    검수요청
                                  </Button>
                                )}
                              {template.inspectionStatus === "REQ" && (
                                <Button
                                  variant="contained"
                                  onClick={() =>
                                    handleInspectionRequestCancel(index)
                                  }
                                >
                                  검수취소
                                </Button>
                              )}
                              {template.templateType === "friendtalk" ||
                              (template.templateType === "alimtalk" &&
                                (template.inspectionStatus === "REG" ||
                                  template.inspectionStatus === "REJ")) ? (
                                <Button
                                  variant="contained"
                                  onClick={() =>
                                    setTemplateEditMode(false, template, true, false)
                                  }
                                >
                                  수정
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  onClick={() =>
                                    setTemplateEditMode(true, template, false, false)
                                  }
                                >
                                  상세보기
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                onClick={() =>
                                  setTemplateEditMode(false, template, false, true)
                                }
                              >
                                복제
                              </Button>
                              {template.templateType === "friendtalk" ||
                              (template.templateType === "alimtalk" &&
                                (template.inspectionStatus === "REG" ||
                                  template.inspectionStatus === "REJ")) ? (
                                <Button
                                  variant="contained"
                                  onClick={() => handleTemplateDelete(index)}
                                  color="error"
                                >
                                  삭제
                                </Button>
                              ) : null}
                            </ButtonGroup>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8}>No Contents available.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <TablePagination
          style={{ overflowY: "hidden" }}
          component="div"
          count={filteredTemplates.length}
          page={currentPage}
          onPageChange={(event, newPage) => setCurrentPage(newPage)}
          rowsPerPage={itemsPerPage}
          onRowsPerPageChange={(event) =>
            setItemsPerPage(parseInt(event.target.value, 10))
          }
          rowsPerPageOptions={[]}
        />
        <DialogActions>
          <IconButton
            autoFocus
            onClick={() => closeModal("templateModal")}
            color="primary"
            sx={{
              position: "absolute",
              left: "98%",
              top: "-40px",
              transform: "scale(1.5)",
              color: "lightgrey",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogActions>
      </StyledDialog>
      <AlertDialog
        open={alertDialog}
        title="삭제하시겠습니까?"
        description="삭제된 템플릿은 복구할 수 없습니다."
        onClose={() => setAlertDialog(false)}
        onConfirm={agreeAlertDialog}
        onCancel={() => setAlertDialog(false)}
      />
      <NewTemplateDialog />
    </>
  );
}

export default ManageTemplateModal;
